import React, { useState } from "react";
import axios from "axios";
import { X, Plus, Minus, Trash2, ShoppingBag, Compass, Loader2 } from "lucide-react";
import { useCart } from "../context/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CourageCart = () => {
  const { items, isOpen, closeCart, updateQty, removeItem, subtotal, count, clearCart } =
    useCart();
  const [loading, setLoading] = useState(false);

  const handleCheckout = async () => {
    const liveItems = items.filter((i) => i.variantId);
    // Demo products (no Shopify variant yet)
    if (liveItems.length === 0) {
      alert(
        "This is a demo store. Once products are published in your Shopify store, checkout will take customers to secure Shopify checkout."
      );
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/shop/checkout`, {
        items: liveItems.map((i) => ({ variantId: i.variantId, quantity: i.qty })),
      });
      if (res.data?.checkoutUrl) {
        window.location.href = res.data.checkoutUrl;
      } else {
        alert("Could not start checkout. Please try again.");
      }
    } catch (e) {
      alert("Checkout is not available yet. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        className={`fixed inset-0 z-[90] bg-black/50 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeCart}
      />

      {/* Drawer */}
      <aside
        className={`fixed top-0 right-0 z-[95] h-full w-full max-w-md bg-white shadow-2xl flex flex-col transition-transform duration-300 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-hidden={!isOpen}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-5"
          style={{ background: "linear-gradient(135deg, #7a3d1f 0%, #a8532a 100%)" }}
        >
          <div className="flex items-center gap-2.5 text-white">
            <Compass className="w-6 h-6" style={{ color: "#F1C91F" }} />
            <div>
              <p className="font-display text-xl leading-none">Courage Cart</p>
              <p className="text-white/80 text-xs mt-1">
                {count} {count === 1 ? "item" : "items"}
              </p>
            </div>
          </div>
          <button
            onClick={closeCart}
            className="flex items-center justify-center w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors"
            aria-label="Close cart"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center px-6">
              <span
                className="flex items-center justify-center w-20 h-20 rounded-full mb-5"
                style={{ backgroundColor: "rgba(255,108,16,0.12)" }}
              >
                <ShoppingBag className="w-10 h-10" style={{ color: "#FF6C10" }} />
              </span>
              <p className="font-display text-2xl" style={{ color: "#187EE4" }}>
                Your Courage Cart is empty
              </p>
              <p className="mt-2 text-slate-500 font-medium">
                Add some brave gear to get started on the adventure!
              </p>
            </div>
          ) : (
            <ul className="space-y-4">
              {items.map((item) => (
                <li
                  key={item.id}
                  className="flex gap-4 rounded-2xl border border-slate-100 p-3 shadow-sm"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 rounded-xl object-cover bg-slate-50 shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-bold text-slate-800 text-sm leading-snug">
                        {item.name}
                      </h4>
                      <button
                        onClick={() => removeItem(item.id)}
                        className="text-slate-400 hover:text-[#FF1228] transition-colors shrink-0"
                        aria-label={`Remove ${item.name}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-3">
                      <div className="flex items-center rounded-full border border-slate-200">
                        <button
                          onClick={() => updateQty(item.id, item.qty - 1)}
                          className="flex items-center justify-center w-8 h-8 text-slate-600 hover:text-[#187EE4]"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-bold text-slate-800">
                          {item.qty}
                        </span>
                        <button
                          onClick={() => updateQty(item.id, item.qty + 1)}
                          className="flex items-center justify-center w-8 h-8 text-slate-600 hover:text-[#187EE4]"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer / checkout */}
        {items.length > 0 && (
          <div className="border-t border-slate-100 px-5 py-5 space-y-4">
            <button
              onClick={handleCheckout}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 font-display text-white text-lg py-4 rounded-full transition-transform hover:scale-[1.02] shadow-lg disabled:opacity-70"
              style={{ backgroundColor: "#FF6C10" }}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Starting checkout…
                </>
              ) : (
                <>Checkout</>
              )}
            </button>
            <button
              onClick={clearCart}
              className="w-full text-slate-400 hover:text-slate-600 text-sm font-semibold transition-colors"
            >
              Clear cart
            </button>
          </div>
        )}
      </aside>
    </>
  );
};

export default CourageCart;
